import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import useAppStore from '../../../store';
import DataModeBadge from '../../../components/feedback/DataModeBadge';
import { formatDistanceToNow } from 'date-fns';
import {
  LLM_PROVIDERS,
  listByoKeys,
  registerByoKey,
  revokeByoKey
} from '../../../lib/api/settings';

/**
 * LLM Providers & Keys tab (full-live-cutover §3, Settings build).
 *
 * Wired to the EXISTING L8 sealed store (/api/llm/byo/keys): register/
 * replace per provider, revoke, and a MASKED list (provider + enabled +
 * rotatedAt — the API has no plaintext read path at all, so the UI can never
 * show one). The screen says so in-UI. Key custody per the §6 decision:
 * tenant BYO keys here; platform keys are host-env and appear nowhere.
 *
 * Fetch-first + optimistic: the masked list loads on mount (live/sample
 * badge); add/replace optimistically marks the provider pending-sealed and
 * rolls the row back if the POST fails; revoke rolls back identically.
 */
function LLMKeysTab() {
  const session = useAppStore((state) => state.session);
  const canManage = session?.role === 'agency-admin' || session?.role === 'operator';

  // null = loading; [] = live-empty; array = masked rows (live).
  const [keys, setKeys] = useState(null);
  const [mode, setMode] = useState('live');

  const [provider, setProvider] = useState('');
  const [keyValue, setKeyValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  // provider -> previous masked row (rollback on failed register/revoke).
  const rollbackRef = React.useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    listByoKeys()
      .then((rows) => {
        if (cancelled) return;
        setKeys(Array.isArray(rows) ? rows : []);
        setMode('live');
      })
      .catch(() => {
        if (cancelled) return;
        setKeys(null);
        setMode('sample');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedProvider = LLM_PROVIDERS.find((entry) => entry.id === provider) || null;

  const handleRegister = async (event) => {
    event.preventDefault();
    if (!selectedProvider || !keyValue.trim() || busy) return;

    const target = selectedProvider.id;
    const previous = (keys || []).find((row) => row.provider === target) || null;
    rollbackRef.current.set(target, previous);

    // Optimistic masked row: the key itself is NEVER held in list state.
    const optimistic = {
      provider: target,
      enabled: true,
      rotatedAt: new Date().toISOString(),
      createdAt: previous?.createdAt || new Date().toISOString(),
      pending: true
    };
    setKeys((current) => [
      ...((current || []).filter((row) => row.provider !== target)),
      optimistic
    ]);
    const keyPayload = keyValue.trim();
    setKeyValue('');
    setBusy(true);
    setFormError('');

    try {
      await registerByoKey(target, keyPayload);
      setKeys((current) => (current || []).map((row) => (
        row.provider === target ? { ...row, pending: false } : row
      )));
    } catch (registerError) {
      const snapshot = rollbackRef.current.get(target);
      setKeys((current) => {
        const rest = (current || []).filter((row) => row.provider !== target);
        return snapshot ? [...rest, snapshot] : rest;
      });
      setFormError(registerError.message || 'Could not register the key — try again.');
    } finally {
      rollbackRef.current.delete(target);
      setBusy(false);
    }
  };

  const handleRevoke = async (target) => {
    if (busy) return;
    const previous = (keys || []).find((row) => row.provider === target) || null;
    rollbackRef.current.set(target, previous);
    setKeys((current) => (current || []).filter((row) => row.provider !== target));

    try {
      await revokeByoKey(target);
    } catch (revokeError) {
      const snapshot = rollbackRef.current.get(target);
      if (snapshot) {
        setKeys((current) => [...(current || []), snapshot]);
      }
      setFormError(revokeError.message || 'Could not revoke the key — try again.');
    } finally {
      rollbackRef.current.delete(target);
    }
  };

  return (
    <div className="settings-tab">
      <section className="settings-section">
        <div className="section-heading">
          <div>
            <h2>
              LLM providers &amp; keys{' '}
              <DataModeBadge
                mode={mode}
                label={mode === 'live' ? null : 'Key store unreachable — list not shown'}
              />
            </h2>
            <span>
              Bring your own key per provider — your key overrides the
              platform default for your tenant&apos;s calls. Keys are sealed
              AES-256-GCM server-side before storage and can never be read
              back, only replaced or revoked.
            </span>
          </div>
        </div>

        {canManage ? (
          <form className="settings-key-form" onSubmit={handleRegister}>
            <label htmlFor="llm-provider">Provider</label>
            <select
              id="llm-provider"
              onChange={(event) => setProvider(event.target.value)}
              value={provider}
            >
              <option value="">Select a provider…</option>
              {LLM_PROVIDERS.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label} — {entry.hint}
                </option>
              ))}
            </select>
            <label htmlFor="llm-key">API key</label>
            <input
              autoComplete="off"
              id="llm-key"
              onChange={(event) => setKeyValue(event.target.value)}
              placeholder={selectedProvider ? `${selectedProvider.label} API key` : 'Select a provider first'}
              type="password"
              value={keyValue}
            />
            <button className="portal-primary-button" disabled={!provider || !keyValue.trim() || busy} type="submit">
              {busy ? 'Sealing…' : 'Add / replace key'}
            </button>
            {formError && <span className="portal-error" role="alert">{formError}</span>}
          </form>
        ) : (
          <p className="settings-note">Only agency roles manage provider keys.</p>
        )}

        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th scope="col">Provider</th>
                <th scope="col">Key</th>
                <th scope="col">Rotated</th>
                {canManage && <th scope="col"><span className="visually-hidden">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {keys === null && (
                <tr>
                  <td colSpan={canManage ? 4 : 3}>
                    {mode === 'sample'
                      ? 'Key store unreachable from this session — no sample keys are fabricated.'
                      : 'Loading…'}
                  </td>
                </tr>
              )}
              {keys !== null && keys.length === 0 && (
                <tr><td colSpan={canManage ? 4 : 3}>No BYO keys registered — platform keys are in use.</td></tr>
              )}
              {(keys || []).map((row) => {
                const label = LLM_PROVIDERS.find((entry) => entry.id === row.provider)?.label || row.provider;
                return (
                  <tr key={row.provider}>
                    <td>{label}</td>
                    <td>
                      <code>•••• sealed</code>
                      {row.pending && <em> sealing…</em>}
                      {row.enabled === false && <em> (revoked)</em>}
                    </td>
                    <td>
                      {row.rotatedAt
                        ? formatDistanceToNow(new Date(row.rotatedAt), { addSuffix: true })
                        : '—'}
                    </td>
                    {canManage && (
                      <td>
                        <button
                          className="settings-text-button"
                          disabled={busy || row.pending}
                          onClick={() => handleRevoke(row.provider)}
                          type="button"
                        >
                          Revoke
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="settings-note">
          Custody (decided 2026-09-21): tenant BYO keys live in the sealed
          store above; platform-owned keys are injected from the host
          environment and are never editable here.
        </p>
      </section>
    </div>
  );
}

export default LLMKeysTab;
